import { createAsyncThunk } from '@reduxjs/toolkit';
import _orderBy from 'lodash/orderBy';
import _flatten from 'lodash/flatten';

import { TRAINING_STRUCTURES } from 'constants/common';

import { updateLessonStatusViaApi } from 'store/helpers/updateLessonStatusViaApi';

import * as api from 'api/methods';

import * as types from './types';

const _getTrainingModules = async (trainingId: string) => {
  const promises = [
    api.getTrainingModules(trainingId),
    api.getTrainingLessons(trainingId),
  ];

  const [modules, lessons] = await Promise.all(promises);

  return _orderBy(modules, ['order'], ['asc']).map((module: any) => ({
    ...module,
    lessons: lessons.filter((lesson: any) => lesson.moduleId === module.id),
  }));
};

export const getTrainingById = createAsyncThunk<any, any>(
  types.GET_CURRENT_TRAINING,
  async (trainingId: string) => {
    const training = await api.getTrainingById(trainingId);

    if (training.structure === TRAINING_STRUCTURES.MODULES) {
      const modules = await _getTrainingModules(trainingId);

      return {
        ...training,
        modules,
      };
    }

    if (training.structure === TRAINING_STRUCTURES.LESSONS) {
      const lessons = await api.getTrainingLessons(trainingId);

      return { ...training, lessons };
    }

    return training;
  },
);

export const updateTrainingById = createAsyncThunk<any, any>(
  types.UPDATE_TRAINING_BY_ID,
  async ({ trainingId, data }: { trainingId: string; data: any }) => {
    const training = await api.updateTrainingById({
      trainingId,
      data,
    });

    if (data.structure && data.structure === TRAINING_STRUCTURES.MODULES) {
      const modules = await _getTrainingModules(trainingId);

      return { ...training, modules };
    }

    if (data.structure === TRAINING_STRUCTURES.LESSONS) {
      const lessons = await api.getTrainingLessons(trainingId);

      return { ...training, lessons };
    }

    return training;
  },
);

export const updateTrainingStatus = createAsyncThunk<any, any>(
  types.UPDATE_TRAINING_STATUS,
  async (
    {
      trainingId,
      withLessons,
      status,
    }: { trainingId: string; withLessons: any; status: any },
    { getState }: { getState: any },
  ) => {
    if (withLessons) {
      const state = getState() as any;

      let lessons = [];

      if (state.currentTraining.structure === TRAINING_STRUCTURES.MODULES) {
        lessons = _flatten(
          state.currentTraining.modules.map((module: any) => module.lessons),
        );
      } else if (
        state.currentTraining.structure === TRAINING_STRUCTURES.LESSONS
      ) {
        lessons = state.currentTraining.lessons;
      }

      await Promise.all(
        lessons.map((lesson: any) =>
          updateLessonStatusViaApi({
            lessonId: lesson.id,
            lessonType: lesson.type,
            data: { status },
          }),
        ),
      );
    }

    const training = await api.updateTrainingById({
      trainingId,
      data: { status },
    });

    return {
      ...training,
      bulkLessonStatus: withLessons ? status : undefined,
    };
  },
);

export const createTrainingModule = createAsyncThunk<any, any>(
  types.CREATE_TRAINING_MODULE,
  async ({ trainingId, data }: { trainingId: string; data: any }) => {
    const module = await api.createTrainingModule({
      trainingId,
      data,
    });

    return module;
  },
);

export const updateTrainingModuleById = createAsyncThunk<any, any>(
  types.UPDATE_TRAINING_MODULE_BY_ID,
  async ({
    trainingId,
    moduleId,
    data,
  }: {
    trainingId: string;
    moduleId: string;
    data: any;
  }) => {
    const module = await api.updateTrainingModuleById({
      trainingId,
      moduleId,
      data,
    });

    return module;
  },
);

export const removeTrainingModuleById = createAsyncThunk<any, any>(
  types.REMOVE_TRAINING_MODULE_BY_ID,
  async ({
    trainingId,
    moduleId,
  }: {
    trainingId: string;
    moduleId: string;
  }) => {
    const module = await api.removeTrainingModuleById({ trainingId, moduleId });

    return module;
  },
);

export const reorderTrainingLessons = createAsyncThunk<any, any>(
  types.REORDER_TRAINING_LESSONS,
  async ({ trainingId, data }: { trainingId: string; data: any }) => {
    const lessons = await api.reorderTrainingLessons({ trainingId, data });

    return { moduleId: data.moduleId, lessons };
  },
);

export const reorderTrainingModules = createAsyncThunk<any, any>(
  types.REORDER_TRAINING_MODULES,
  async ({ trainingId, data }: { trainingId: string; data: any }) => {
    const modules = await api.reorderTrainingModules({ trainingId, data });

    return modules;
  },
);
